import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import api from '../../services/api.js';
import { APP_MENU } from './menu.js';
import { canAccess } from '../../config/rbac.js';
import HelpDrawer from '../help/HelpDrawer.jsx';

/**
 * Filters the nav model to the items the given role may access — leaves are
 * checked against config/rbac.js, and a parent is dropped when none of its
 * children remain visible. Keeps the menu in sync with the RoleRoute guard.
 */
function visibleMenu(role) {
  return APP_MENU.map((item) => {
    if (item.children) {
      const children = item.children.filter((c) => canAccess(role, c.to));
      return children.length ? { ...item, children } : null;
    }
    return canAccess(role, item.to) ? item : null;
  }).filter(Boolean);
}

// Real-time personal credit balance shown in the header. Refreshes when any page
// dispatches a `credits:changed` event (no page reload needed).
function CreditBalance() {
  const [balance, setBalance] = useState(null);

  const refresh = useCallback(() => {
    api
      .myCredit()
      .then((acct) => setBalance(acct.balance))
      .catch(() => setBalance(null));
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('credits:changed', refresh);
    return () => window.removeEventListener('credits:changed', refresh);
  }, [refresh]);

  if (balance === null) return null;

  return (
    <Link to="/my-credit-balance" className="text-white text-decoration-none me-3 small text-nowrap d-none d-sm-inline">
      <i className="fa fa-wallet me-1" />
      Credits: <strong>{balance.toLocaleString()}</strong>
    </Link>
  );
}

/**
 * Vertical sidebar navigation. Flat items render as a single link; grouped items
 * render a section label followed by their (role-filtered) child links. A
 * vertical list scales to any number of sections without the horizontal-overflow
 * problems of a top bar.
 */
function SidebarNav({ role, onNavigate }) {
  return (
    <ul className="app-sidebar-nav list-unstyled mb-0">
      {visibleMenu(role).map((item) =>
        item.children ? (
          <li key={item.label} className="app-sidebar-group">
            <div className="app-sidebar-group-label">
              {item.icon && <i className={`${item.icon} me-2`} />}
              {item.label}
            </div>
            <ul className="list-unstyled mb-0">
              {item.children.map((child) => (
                <li key={child.label}>
                  <NavLink className="app-sidebar-link" to={child.to} onClick={onNavigate}>
                    {child.icon && <i className={`${child.icon} app-sidebar-icon`} />}
                    {child.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
        ) : (
          <li key={item.label}>
            <NavLink className="app-sidebar-link app-sidebar-link--top" to={item.to} onClick={onNavigate}>
              {item.icon && <i className={`${item.icon} app-sidebar-icon`} />}
              {item.label}
            </NavLink>
          </li>
        )
      )}
    </ul>
  );
}

/**
 * Authenticated application shell: a slim fixed top header (brand + credits +
 * help + user menu) and a fixed left sidebar for navigation. On narrow screens
 * the sidebar slides in over a backdrop, toggled from the header.
 */
export default function AppShell() {
  const { user, logout } = useAuth();
  const [showHelp, setShowHelp] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const location = useLocation();

  // Close the mobile sidebar whenever the route changes.
  useEffect(() => {
    setShowSidebar(false);
  }, [location.pathname]);

  const initial = (user?.name || user?.email || 'U').charAt(0).toUpperCase();

  return (
    <div className="app-shell-layout">
      {/* Top header */}
      <header className="app-header navbar navbar-dark bg-primary fixed-top">
        <div className="container-fluid">
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-sm btn-outline-light d-xl-none me-2"
              onClick={() => setShowSidebar((s) => !s)}
              aria-label="Toggle navigation"
            >
              <i className="fa fa-bars" />
            </button>
            <Link className="navbar-brand fw-bold mb-0" to="/dashboard">
              SMS Gateway
            </Link>
          </div>

          <div className="d-flex align-items-center">
            <CreditBalance />
            <button
              type="button"
              className="btn btn-sm btn-outline-light me-2 rounded-circle flex-shrink-0"
              onClick={() => setShowHelp(true)}
              title="Help & Documentation"
              aria-label="Open help"
              style={{ width: 32, height: 32, lineHeight: 1, fontWeight: 700 }}
            >
              ?
            </button>
            <div className="dropdown">
              <button
                className="btn btn-sm btn-outline-light dropdown-toggle d-flex align-items-center"
                type="button"
                id="userMenu"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span
                  className="rounded-circle bg-white text-primary fw-bold d-inline-flex align-items-center justify-content-center me-2"
                  style={{ width: 24, height: 24, fontSize: 12 }}
                  aria-hidden="true"
                >
                  {initial}
                </span>
                <span className="d-none d-sm-inline text-nowrap">
                  {user?.name || user?.email || 'User'}
                </span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userMenu">
                <li>
                  <NavLink className="dropdown-item" to="/my-profile">
                    <i className="fa fa-user me-2" />
                    My Profile
                  </NavLink>
                </li>
                <li>
                  <NavLink className="dropdown-item" to="/my-credit-balance">
                    <i className="fa fa-wallet me-2" />
                    My Credits
                  </NavLink>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <button type="button" className="dropdown-item" onClick={logout}>
                    <i className="fa fa-sign-out-alt me-2" />
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar + content */}
      {showSidebar && (
        <div className="app-sidebar-backdrop d-xl-none" onClick={() => setShowSidebar(false)} aria-hidden="true" />
      )}
      <aside className={`app-sidebar${showSidebar ? ' app-sidebar--open' : ''}`}>
        <SidebarNav role={user?.role} onNavigate={() => setShowSidebar(false)} />
      </aside>

      <main className="app-main">
        <div className="container-fluid py-4">
          <Outlet />
        </div>
      </main>

      <HelpDrawer isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
}
